import type { Bill } from '@/types/bill'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import { Badge } from "@/components/ui/badge"
import { ArrowUpCircle, ArrowDownCircle, CircleDot, School, Building2 } from "lucide-react"
import { cn } from "@/lib/utils"

interface BillImpactDialogProps {
  bill: Bill | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface ImpactScoreProps {
  type: 'positive' | 'negative' | 'neutral'
  score: number
  label: string
}

const ImpactScore = ({ type, score, label }: ImpactScoreProps) => {
  const Icon = {
    positive: ArrowUpCircle,
    negative: ArrowDownCircle,
    neutral: CircleDot
  }[type]

  const styles = {
    positive: "bg-green-100 text-green-800",
    negative: "bg-red-100 text-red-800",
    neutral: "bg-gray-100 text-gray-800"
  }[type]

  return (
    <div className="flex items-center gap-2">
      <Badge variant="secondary" className={cn(styles)}>
        <Icon className="h-4 w-4 mr-1" />
        {score}
      </Badge>
      <span className="text-sm text-muted-foreground">{label}</span>
    </div>
  )
}

export function BillImpactDialog({ bill, open, onOpenChange }: BillImpactDialogProps) {
  if (!bill?.monitor_analysis) return null

  const { analysis, summary } = bill.monitor_analysis

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-3">
        <DialogHeader className="pb-1 mb-0">
          <DialogTitle>Impact Analysis: {bill.billNumber}</DialogTitle>
        </DialogHeader>

        <div className="grid gap-0 py-1">
          {/* Overall Impact Card */}
          <Card style={{ marginBottom: '-1.5rem' }}>
            <CardHeader className="pb-1 p-2">
              <CardTitle className="text-xl">Overall Impact</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-2 p-2 pt-0">
              <div className="flex items-center gap-2">
                <ImpactScore 
                  type={summary.overall_impact.type}
                  score={summary.overall_impact.score}
                  label="Overall Impact Score"
                />
                <Badge variant={summary.should_monitor ? "default" : "secondary"}>
                  {summary.should_monitor ? "Monitoring" : "Not Monitoring"}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* UMCP Impact Card */}
          <Card style={{ marginTop: '-1.5rem', marginBottom: '-1.5rem' }}>
            <CardHeader className="pb-1 p-2">
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                <CardTitle className="text-xl">UMD College Park Impact</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-2 p-2 pt-0">
              <div className="flex flex-col gap-2">
                <ImpactScore 
                  type={analysis.college_park.impacts.direct.type}
                  score={analysis.college_park.impacts.direct.score}
                  label="Direct Impact"
                />
                <ImpactScore 
                  type={analysis.college_park.impacts.indirect.type}
                  score={analysis.college_park.impacts.indirect.score}
                  label="Indirect Impact"
                />
              </div>
              <Separator />
              <div className="text-sm">
                <h4 className="font-semibold mb-1">Rationale:</h4>
                <p className="text-muted-foreground">{analysis.college_park.rationale}</p>
              </div>
            </CardContent>
          </Card>

          {/* Higher Education Impact Card */}
          <Card style={{ marginTop: '-1.5rem' }}>
            <CardHeader className="pb-1 p-2">
              <div className="flex items-center gap-2">
                <School className="h-5 w-5" />
                <CardTitle className="text-xl">Higher Education Impact</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-2 p-2 pt-0">
              <div className="flex flex-col gap-2">
                <ImpactScore 
                  type={analysis.higher_education.impacts.direct.type}
                  score={analysis.higher_education.impacts.direct.score}
                  label="Direct Impact"
                />
                <ImpactScore 
                  type={analysis.higher_education.impacts.indirect.type}
                  score={analysis.higher_education.impacts.indirect.score}
                  label="Indirect Impact"
                />
              </div>
              <Separator />
              <div className="text-sm">
                <h4 className="font-semibold mb-1">Rationale:</h4>
                <p className="text-muted-foreground">{analysis.higher_education.rationale}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  )
}